import React, { useState, useEffect } from 'react';
import styles from './MobileCustomerMenu.module.scss';
import { menuItems } from './CustomerMenu.constants';
import { useNavigate } from 'react-router-dom';
import { useSubMenuVisibility } from './customerMenu.visibility';
import { useCanSeeMenuItem } from '../../../guards/useCanSeeMenuItem';
import { LogoutIcon } from '../../../assets/customermenu-icons';
import { ApplicationRoutes } from '../../../routes';

interface SubMenuItem {
  id: string;
  label: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  subMenuItems?: SubMenuItem[];
}

interface MobileCustomerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  activeMenu?: string;
  activeSubMenu?: string;
  onMenuClick?: (menuId: string) => void;
  onSubMenuClick?: (subMenuId: string) => void;
  onLogOutClick?: () => void;
  userName?: string;
  userAvatar?: string;
}

const MobileCustomerMenu: React.FC<MobileCustomerMenuProps> = ({
  isOpen,
  onClose,
  activeMenu = 'dashboard',
  activeSubMenu,
  onMenuClick,
  onSubMenuClick,
  onLogOutClick,
  userName = 'Customer',
  userAvatar,
}) => {
  const navigate = useNavigate();
  const { filterVisibleItems } = useSubMenuVisibility();
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // Close expanded menu when modal closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedMenu(null);
    }
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const canSeeMenuItem = useCanSeeMenuItem();
  const _menuItems = menuItems.filter(canSeeMenuItem);

  const handleMenuClick = (menuId: string, hasSubMenu: boolean | undefined, path: string) => {
    // Toggle expanded state if menu has submenu
    if (hasSubMenu) {
      setExpandedMenu(expandedMenu === menuId ? null : menuId);
    } else {
      setExpandedMenu(null);
    }
    
    if (path) {
      navigate(path);
      onClose();
    }
    onMenuClick?.(menuId);
  };

  const handleSubMenuClick = (subMenuId: string, path: string) => {
    if (path) {
      navigate(path)
      onClose();
    }
    onSubMenuClick?.(subMenuId);
     // Close menu after selecting submenu item
  };

  const handleAvatarClick = () => {
     navigate(`/${ApplicationRoutes.SETTINGS}`);
     onClose();
  }

  if (!isOpen) return null;

  return (
    <div className={styles.mobileMenuOverlay} onClick={onClose}>
      <div 
        className={styles.mobileMenu} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className={styles.closeButton} onClick={onClose} aria-label="Close menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Menu Items */}
        <nav className={styles.menuNav}>
          <div className={styles.menuItems}>
            {_menuItems.map((item) => {
              const visibleSubMenuItems = filterVisibleItems(item.subMenuItems);
              const hasSubMenu = visibleSubMenuItems && visibleSubMenuItems.length > 0;
              const isExpanded = expandedMenu === item.id;
              const isActive = activeMenu === item.id;

              return (
                <div key={item.id} className={styles.menuItemWrapper}>
                  <button
                    className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
                    onClick={() => handleMenuClick(item.id, hasSubMenu, item.path)}
                  >
                    <div className={styles.menuIcon}>{isActive? item.activeIcon: item.icon}</div>
                    <span className={styles.menuLabel}>{item.label}</span>
                    {hasSubMenu && (
                      <div
                        className={`${styles.chevronIcon} ${
                          isExpanded ? styles.expanded : ''
                        }`}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M5 7.5L10 12.5L15 7.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </button>

                  {/* Submenu Items */}
                  {hasSubMenu && isExpanded && (
                    <div className={styles.subMenuItems}>
                      {visibleSubMenuItems.map((subItem) => (
                        <button
                          key={subItem.id}
                          className={`${styles.subMenuItem} ${
                            activeSubMenu === subItem.id ? styles.active : ''
                          }`}
                          onClick={() => handleSubMenuClick(subItem.id, subItem.path)}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className={styles.logoutSection}>
          <button className={styles.logoutBtn} onClick={onLogOutClick}>
            <div className={styles.menuIcon}><LogoutIcon /></div>
            <span className={styles.menuLabel}>Logout</span>
          </button>
        </div>

        {/* User Profile - Fixed at Bottom */}
        <div className={styles.userProfile}>
          <div onClick={handleAvatarClick} className={styles.avatar}>
            {userAvatar ? <img src={userAvatar} alt={userName} className={styles.avatarImage} /> : userName.charAt(0).toUpperCase()}
          </div>
          <span className={styles.userName}>{userName}</span>
        </div>
      </div>
    </div>
  );
};

export default MobileCustomerMenu;